import { makeStyles } from '@material-ui/core';

// Visual conventions are taken from the tabs' own GenericTableStyles (#C6C6C6
// borders, #F9F9FC header fill, 10px cell padding, #1976d2 links) so the
// Documents tab reads as one of the batch tabs rather than a separate screen.
export const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    // The first section's numbered circle sits flush against the top of the tab
    // panel, and the panel's own wrapper computes to overflow-y: auto (it sets
    // overflow-x: hidden, which per spec promotes the other axis from visible to
    // auto), so a flush circle gets its top few pixels clipped. This is the
    // clearance that keeps it whole.
    // Old value: (none)
    paddingTop: '6px',
  },

  // ---- numbered section rail (FSD §3) --------------------------------------
  // Each section is a row: the numbered circle + its connecting line on the
  // left, the section's heading and table on the right.
  section: {
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
    width: '48px',
  },
  railNumber: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    backgroundColor: '#2196C4',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight: 600,
    flexShrink: 0,
    marginTop: '2px',
  },
  // The vertical line beneath a circle, joining it to the next section. Grows to
  // fill whatever height the section's table takes.
  railLine: {
    width: '4px',
    flex: 1,
    backgroundColor: '#2196C4',
    borderRadius: '2px',
    marginTop: '4px',
    marginBottom: '4px',
    minHeight: '16px',
  },
  sectionBody: {
    flex: 1,
    minWidth: 0,
    paddingBottom: '20px',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#1B1B1B',
    marginBottom: '10px',
    lineHeight: '30px',
  },

  // ---- per-section table ---------------------------------------------------
  tableWrap: {
    border: '1px solid #C6C6C6',
    borderRadius: '2px',
    // Wide content scrolls inside its own container so the tab (and the page)
    // never scrolls horizontally.
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '760px',
  },
  th: {
    backgroundColor: '#F9F9FC',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    color: '#1B1B1B',
    textAlign: 'left',
    padding: '10px',
    borderBottom: '1px solid #C6C6C6',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '10px',
    fontSize: '0.875rem',
    color: '#1B1B1B',
    borderBottom: '1px solid #C6C6C6',
    verticalAlign: 'middle',
  },
  // The Record ID cell spans its group's document rows (FSD BR-DOC-DEV-01: the
  // ID is shown once per group with each document as a sub-row beneath it), so
  // it carries the group's right-hand divider and no bottom border of its own.
  recordIdCell: {
    padding: '10px',
    fontSize: '0.875rem',
    color: '#1B1B1B',
    fontWeight: 500,
    borderBottom: '1px solid #C6C6C6',
    borderRight: '1px solid #C6C6C6',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  // Rows within one Record ID group are separated by a lighter rule than the
  // group boundary, so the grouping stays legible.
  innerRowCell: {
    borderBottom: '1px solid #E8E8E8',
  },
  lastRowCell: {
    borderBottom: 'none',
  },

  // ---- Download Document cell ---------------------------------------------
  downloadCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  downloadIcon: {
    color: '#1976d2',
    fontSize: '1.25rem',
    flexShrink: 0,
  },
  fileLink: {
    color: '#1976d2',
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    textAlign: 'left',
    '&:hover': {
      color: '#12558f',
    },
    '&:disabled': {
      color: '#9E9E9E',
      cursor: 'default',
      textDecoration: 'none',
    },
  },

  // ---- Review Digitalized Document cell (FSD §4) ---------------------------
  reviewCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // Orange when at least one field still needs review (FSD §7: "flagged with an
  // alert (orange) icon when the requiring-review count is greater than zero"),
  // green once nothing is outstanding.
  reviewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    padding: '2px 4px',
    borderRadius: '4px',
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
    '&:focus-visible': {
      outline: '2px solid #1976d2',
      outlineOffset: '1px',
    },
  },
  reviewIconFlagged: {
    color: '#E8590C',
    fontSize: '1.25rem',
    flexShrink: 0,
  },
  reviewIconClear: {
    color: '#2E7D32',
    fontSize: '1.25rem',
    flexShrink: 0,
  },
  reviewCounts: {
    fontSize: '0.75rem',
    lineHeight: 1.45,
    color: '#1B1B1B',
    whiteSpace: 'nowrap',
  },
  reviewCountsMuted: {
    color: '#6B6B6B',
  },

  // ---- states --------------------------------------------------------------
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    gap: '12px',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
    color: '#666',
    fontSize: '0.875rem',
  },
}));

// ---- split-screen review window (FSD §4) -----------------------------------
// Left pane: the digitalized fields. Right pane: the source PDF. Fixed at 50/50
// per BR-DOC-REVIEW-02's "fields on the left, source PDF on the right",
// mirroring the layout of the existing commercial two-page review screen.
export const useReviewStyles = makeStyles((theme) => ({
  dialogPaper: {
    backgroundColor: '#fff',
    // Needed only by the stacked (narrow) layout below, where the two panes no
    // longer share one viewport height and the window has to scroll. In the
    // side-by-side layout the content always fits, so this never engages.
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 20px',
    // The rule under the header is a <Divider> in the component, not a border here.
    flexShrink: 0,
  },
  // Batch context strip — the same fields the batch header bar shows, so the
  // reviewer can see which batch's document they are looking at.
  // Typography here is deliberately the same as the PD project-details strip in
  // projectHistory/ProjectHistoryStyles.js (projectDetailsHeader /
  // projectDetailsValue): one size for both rows, #000, 600 for the label and
  // 400 for the value, on the same #E5F2F7 ground. The strip previously used a
  // smaller bolder label and a greyer value on a different blue, which read as a
  // separate component rather than as the PD header bar it mirrors.
  contextBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '28px',
    // Old value: backgroundColor: '#EEF4FB'
    backgroundColor: '#E5F2F7',
    borderRadius: '4px',
    padding: '10px 18px',
    margin: '12px 20px 0',
  },
  contextItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  contextLabel: {
    fontWeight: 600,
    color: '#000',
    whiteSpace: 'nowrap',
  },
  contextValue: {
    color: '#000',
    whiteSpace: 'nowrap',
  },

  panes: {
    display: 'flex',
    gap: '20px',
    padding: '16px 20px 20px',
    flex: 1,
    minHeight: 0,
    // Stack the panes on narrow screens rather than squeezing both.
    '@media (max-width: 900px)': {
      flexDirection: 'column',
    },
  },
  pane: {
    flex: '1 1 50%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Stacked, the panes must NOT keep splitting one viewport height between
    // them — that collapsed the fields list to a two-row sliver with its own
    // scrollbar. Each pane takes its natural height instead and the window
    // scrolls (see dialogPaper).
    '@media (max-width: 900px)': {
      flex: '0 0 auto',
      minHeight: '420px',
    },
  },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '10px',
    minHeight: '36px',
  },

  // ---- left pane: extracted fields ----------------------------------------
  fieldsBox: {
    border: '1px solid #C6C6C6',
    borderRadius: '4px',
    padding: '12px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
    '@media (max-width: 900px)': {
      // Long field sets still scroll here rather than pushing the PDF pane
      // arbitrarily far down the stacked window.
      maxHeight: '460px',
    },
  },
  // Row rhythm only; the row itself is a MUI Grid, as in CommonDigitizedDocument.
  formField: {
    marginBottom: theme.spacing(2),
  },

  // The three lines that close the left pane — the legend, the review counts and
  // the read-only note — were each set a different way (a Typography body2, then
  // two hand-sized divs at 0.875 and 0.75rem in two different greys). They now
  // share one size and are all rendered through Typography, so weight and colour
  // are the only things that separate them.
  reviewSummary: {
    fontWeight: 600,
    color: '#000',
    marginTop: '10px',
  },
  empty: {
    padding: '24px',
    textAlign: 'center',
    color: '#666',
    fontSize: '0.875rem',
  },
  readOnlyNote: {
    fontStyle: 'italic',
    color: '#6B6B6B',
    marginTop: '6px',
  },

  // ---- right pane: source PDF ---------------------------------------------
  pdfBox: {
    position: 'relative',
    border: '1px solid #C6C6C6',
    borderRadius: '4px',
    overflow: 'hidden',
    flex: 1,
    minHeight: '420px',
    backgroundColor: '#FAFAFA',
  },
  pdfFrame: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  },
  pdfOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '20px',
    textAlign: 'center',
    color: '#666',
    fontSize: '0.875rem',
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  downloadButton: {
    textTransform: 'none',
    backgroundColor: '#E8590C',
    color: '#fff',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#C74A08',
    },
  },
}));
